/**
 * Public flow portfolio hooks — token-based mirror of
 * use-flow-instances/use-portfolios.ts, using publicFlow query keys and the
 * session token. Every mutation invalidates the affected portfolio queries and
 * the public completion preflight (P5).
 */

import type { DynamicRegenSync, SnapshotInvalidation } from "@repo/shared/types";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import * as portfoliosApi from "@/lib/api/public-flow/portfolios";
import { usePublicSession, useSessionToken } from "@/lib/context/public-session-context";
import { seedGoalPortfolios } from "@/lib/hooks/use-flow-instances/use-portfolios";
import {
  publicPortfolioSnapshotQueryKeys,
  useInvalidatePublicAssetClassForecast,
  useInvalidatePublicPortfolioSnapshots,
} from "./use-goal-snapshots";
import { publicFlowCompletionQueryKey } from "./use-public-flow-completion";

// --- Query Keys ---

const keys = {
  // Mirrors the advisor family shapes (use-flow-instances/use-portfolios.ts):
  // the goal's list lives under "goals", and EVERYTHING per-portfolio lives
  // under ["publicFlow", "portfolios", portfolioId, ...] — detail, snapshots
  // (use-portfolio-snapshot.ts) and the frozen existing
  // side — so one detail-prefix invalidation covers them all. The former
  // singular "portfolio" family silently prefix-matched NOTHING of the
  // snapshot queries (stale-Analysis-tab bug, 2026-09-15).
  portfolios: (goalId: string | null) => ["publicFlow", "goals", goalId, "portfolios"] as const,
  // QL-676 — every portfolio of the session's instance, grouped by goal (the
  // Portfolio List). Under the ["publicFlow","goals"] prefix on purpose: every
  // goal-list invalidation refreshed the per-goal lists before, and still
  // reaches this read. Portfolio mutations reach it through
  // `invalidatePublicGoalPortfolios`.
  instancePortfolios: () => ["publicFlow", "goals", "allPortfolios"] as const,
  portfolio: (portfolioId: string | null) => ["publicFlow", "portfolios", portfolioId] as const,
  // QL-72 — nested under the portfolio so its family invalidation covers it.
  // The goal's score and platform decide which candidate is the matching one,
  // so a re-score or platform change must refetch the list.
  modelCandidates: (
    portfolioId: string | null,
    goalRiskScore: number | null,
    platformId: string | null,
  ) =>
    [
      "publicFlow",
      "portfolios",
      portfolioId,
      "modelCandidates",
      goalRiskScore,
      platformId,
    ] as const,
  // QL-362 — prefix of every portfolio detail (see the advisor family): account-
  // minimum violations are a goal-level aggregate, so a composition change in
  // one portfolio can flip its siblings' payloads.
  allPortfolioDetails: () => ["publicFlow", "portfolios"] as const,
};

// --- Invalidation ---

/**
 * QL-676 — public mirror of `invalidateGoalPortfolios`: a goal's portfolio
 * list changed, so invalidate its per-goal list AND the instance-wide read the
 * Portfolio List renders from.
 */
export function invalidatePublicGoalPortfolios(queryClient: QueryClient, goalId: string) {
  queryClient.invalidateQueries({ queryKey: keys.portfolios(goalId) });
  return queryClient.invalidateQueries({ queryKey: keys.instancePortfolios() });
}

/**
 * QL-774 — public mirror of `invalidateInstanceGoalList`: a portfolio was added
 * or removed, so refresh the session's goal list, whose slim `portfolios` feed
 * the conditional rules (`__followup_mode__` among them). Exact, so the per-goal
 * reads nested under ["publicFlow", "goals"] don't all refetch.
 */
export function invalidatePublicGoalList(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: ["publicFlow", "goals"], exact: true });
}

/**
 * Public mirror of `invalidatePortfolioFamily`. QL-362 — account-minimum
 * violations aggregate across the goal's
 * portfolios (shared accounts), so a composition or schedule change in one
 * portfolio leaves its siblings' payloads stale too. Siblings only need their
 * detail refetched, so the predicate stops at the 3-segment detail key — the
 * bare prefix would also match every portfolio's snapshot queries and
 * refetch all analytics on each edit. The changed portfolio gets
 * its whole sub-family (detail + everything nested under it).
 */
export function invalidatePublicPortfolioFamily(
  queryClient: QueryClient,
  changedPortfolioId?: string,
) {
  if (changedPortfolioId) {
    queryClient.invalidateQueries({ queryKey: keys.portfolio(changedPortfolioId) });
  }
  return queryClient.invalidateQueries({
    queryKey: keys.allPortfolioDetails(),
    predicate: (query) => query.queryKey.length === 3,
  });
}

// --- Query Hooks ---

/**
 * QL-72 — public mirror of `useModelPortfolioCandidates`. Reads the session
 * with `usePublicSession` (not `useSessionToken`, which throws) because the
 * shared picker also renders on the advisor side; there the query stays off.
 */
export function usePublicModelPortfolioCandidates(
  portfolioId: string | null,
  options: {
    goalRiskScore: number | null;
    platformId: string | null;
    lang?: string;
  },
) {
  const session = usePublicSession();
  const token = session.isPublic ? session.sessionToken : null;
  return useQuery({
    queryKey: keys.modelCandidates(portfolioId, options.goalRiskScore, options.platformId),
    queryFn: () =>
      portfoliosApi.getPublicModelPortfolioCandidates(portfolioId!, token!, options.lang),
    enabled: !!portfolioId && !!token,
  });
}

export function usePublicFlowPortfolios(goalId: string | null) {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.portfolios(goalId),
    queryFn: () => portfoliosApi.getPublicPortfolios(goalId!, token),
    enabled: !!goalId && !!token,
  });
  const queryClient = useQueryClient();
  const mutate = () => (goalId ? invalidatePublicGoalPortfolios(queryClient, goalId) : undefined);
  return { portfolios: data ?? [], error, isLoading, mutate };
}

/**
 * Portfolios for several goals at once — public mirror of
 * `useFlowInstancePortfoliosForGoals`: one instance-wide request whatever the
 * goal count (QL-676), seeding each goal's `keys.portfolios(goalId)` entry so
 * `usePublicFlowPortfolios` readers share it. With exactly one goal it reads
 * that goal's own list, which the single-goal flow's goal context already
 * reads on every page.
 */
export function usePublicFlowPortfoliosForGoals(goalIds: string[]) {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const singleGoalId = goalIds.length === 1 ? goalIds[0]! : null;
  const single = usePublicFlowPortfolios(singleGoalId);
  const { data, isLoading } = useQuery({
    queryKey: keys.instancePortfolios(),
    // Deliberately does not read the AbortSignal: reading it makes React Query
    // abort the fetch whenever the last observer unmounts. A superseded response
    // is harmless — `seedGoalPortfolios` skips every goal that was invalidated,
    // is refetching, or holds newer data.
    queryFn: async () => {
      const startedAt = Date.now();
      const response = await portfoliosApi.getPublicInstancePortfolios(token);
      seedGoalPortfolios(queryClient, keys.portfolios, response, startedAt);
      return response;
    },
    enabled: !!token && goalIds.length > 1,
  });
  return useMemo(() => {
    if (singleGoalId) return [{ portfolios: single.portfolios, isLoading: single.isLoading }];
    const byGoal = new Map(data?.goals.map((g) => [g.goalId, g.portfolios]));
    return goalIds.map((goalId) => ({ portfolios: byGoal.get(goalId) ?? [], isLoading }));
  }, [data, goalIds, isLoading, singleGoalId, single.portfolios, single.isLoading]);
}

export function usePublicPortfolio(portfolioId: string | null) {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.portfolio(portfolioId),
    queryFn: () => portfoliosApi.getPublicPortfolio(portfolioId!, token),
    enabled: !!portfolioId && !!token,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: keys.portfolio(portfolioId) });
  return { portfolio: data ?? null, error, isLoading, mutate };
}

// --- Mutation Hooks ---

export function usePublicCreatePortfolio(goalId: string) {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      portfoliosApi.createPublicPortfolio(goalId, data, token),
    onSettled: () => {
      invalidatePublicGoalPortfolios(queryClient, goalId);
      invalidatePublicGoalList(queryClient);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

export function usePublicUpdatePortfolio() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();
  return useMutation({
    mutationFn: ({ portfolioId, data }: { portfolioId: string; data: Record<string, unknown> }) =>
      portfoliosApi.updatePublicPortfolio(portfolioId, data, token),
    onSettled: (_data, _error, vars) => {
      invalidatePublicPortfolioFamily(queryClient, vars.portfolioId);
    },
    onSuccess: (data: { goalId?: string; refreshHints?: SnapshotInvalidation }) => {
      if (data?.goalId) {
        invalidatePublicGoalPortfolios(queryClient, data.goalId);
      }
      if (data?.refreshHints) {
        invalidatePortfolioSnapshots(data.refreshHints.portfolioId, data.refreshHints);
      }
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

export function usePublicDeletePortfolio() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidateAssetClassForecast = useInvalidatePublicAssetClassForecast();
  return useMutation({
    mutationFn: ({ portfolioId }: { portfolioId: string; goalId?: string }) =>
      portfoliosApi.deletePublicPortfolio(portfolioId, token),
    onSettled: (_data, _error, vars) => {
      invalidatePublicPortfolioFamily(queryClient, vars.portfolioId);
      invalidatePublicGoalList(queryClient);
      if (vars.goalId) {
        invalidatePublicGoalPortfolios(queryClient, vars.goalId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      // QL-315 — the server regenerates both scopes of the merge after a
      // portfolio delete; this path carries no refreshHints to route it through.
      invalidateAssetClassForecast();
    },
  });
}

export function usePublicAddPortfolioProduct() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();
  return useMutation({
    mutationFn: ({
      portfolioId,
      data,
    }: {
      portfolioId: string;
      goalId?: string;
      data: Record<string, unknown>;
    }) => portfoliosApi.addPublicPortfolioProduct(portfolioId, data, token),
    onSettled: (_data, _error, vars) => {
      invalidatePublicPortfolioFamily(queryClient, vars.portfolioId);
      if (vars.goalId) {
        invalidatePublicGoalPortfolios(queryClient, vars.goalId);
      }
    },
    onSuccess: (data: { refreshHints?: SnapshotInvalidation }) => {
      if (data?.refreshHints) {
        invalidatePortfolioSnapshots(data.refreshHints.portfolioId, data.refreshHints);
      }
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

export function usePublicUpdatePortfolioProduct() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();
  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string;
      portfolioId?: string;
      goalId?: string;
      data: Record<string, unknown>;
    }) => portfoliosApi.updatePublicPortfolioProduct(productId, data, token),
    onSettled: (_data, _error, vars) => {
      invalidatePublicPortfolioFamily(queryClient, vars.portfolioId);
      if (vars.goalId) {
        invalidatePublicGoalPortfolios(queryClient, vars.goalId);
      }
    },
    onSuccess: (data: { refreshHints?: SnapshotInvalidation }) => {
      if (data?.refreshHints) {
        invalidatePortfolioSnapshots(data.refreshHints.portfolioId, data.refreshHints);
      }
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

export function usePublicDeletePortfolioProduct() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();
  return useMutation({
    mutationFn: ({ productId }: { productId: string; portfolioId?: string; goalId?: string }) =>
      portfoliosApi.deletePublicPortfolioProduct(productId, token),
    onSettled: (
      data: { portfolioId?: string; refreshHints?: SnapshotInvalidation } | undefined,
      _error,
      vars,
    ) => {
      invalidatePublicPortfolioFamily(queryClient, data?.portfolioId ?? vars.portfolioId);
      if (vars.goalId) {
        invalidatePublicGoalPortfolios(queryClient, vars.goalId);
      }
      if (data?.refreshHints) {
        invalidatePortfolioSnapshots(data.refreshHints.portfolioId, data.refreshHints);
      }
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

// --- Dynamic regeneration invalidation (QP-185) ---

/**
 * Public mirror of use-flow-instances' useInvalidateDynamicSync: refresh the
 * portfolio queries touched by a server-side dynamic-portfolio regeneration —
 * and the snapshot queries too, since regeneration now also rebuilds the
 * portfolio's analytics snapshots (QP-185).
 */
export function useInvalidatePublicDynamicSync() {
  const queryClient = useQueryClient();
  return (sync?: DynamicRegenSync) => {
    if (!sync?.changed) return;
    for (const portfolioId of sync.portfolioIds) {
      queryClient.invalidateQueries({ queryKey: keys.portfolio(portfolioId) });
      queryClient.invalidateQueries({
        queryKey: publicPortfolioSnapshotQueryKeys.allForPortfolio(portfolioId),
      });
    }
    for (const goalId of sync.goalIds) {
      invalidatePublicGoalPortfolios(queryClient, goalId);
    }
  };
}

// --- Exported Query Keys (for targeted invalidation in widget mutations) ---

export { keys as publicPortfolioKeys };
